import "./HPTable.scss";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  GetAllProfessionalDetail,
} from "../../../utils/apiCalls/healthProfessionals";
import {
  PlusIcon,
} from "../../../assets/icons-components/Icons";
import SearchInput from "../../../components/SearchInput/SearchInput";
import Buttons from "../../../components/Buttons/Buttons";
import { useNavigate } from "react-router-dom";
import CustomTooltip from "../../../components/CustomTooltip/CustomTooltip";
import TableStatusRow from "../../../components/TableStatusRow/TableStatusRow";
import useDebouncedValue from "../../../hooks/useDebouncedValue";

// Persisted across navigating to a HP's patients/edit page and back, so the
// search/page isn't silently reset on return.
const SEARCH_KEY = "hpTableSearch";
const PAGE_KEY = "hpTablePage";

// A user-facing display preference (like column layout in a spreadsheet),
// not transient session state — kept in localStorage so it survives closing
// the tab, unlike the sessionStorage-backed search/page keys above.
const VISIBLE_COLUMNS_KEY = "hpTableVisibleColumns:v1";

// Parses this API's two observed duration shapes into seconds for sorting:
// "H:M:S" (e.g. "140:39:16") and the older "X sessions - Y hours-Z minutes-W
// seconds" form formatDuration() abbreviates. Display always goes through
// formatDuration() instead — this is comparison-only.
const parseDurationToSeconds = (value) => {
  if (!value || value === "-") return -1;
  const str = String(value);
  if (str.includes(":")) {
    const [h = 0, m = 0, s = 0] = str.split(":").map(Number);
    return h * 3600 + m * 60 + s;
  }
  const hours = Number(str.match(/(-?\d+(\.\d+)?)\s*hours?/)?.[1] || 0);
  const minutes = Number(str.match(/(-?\d+(\.\d+)?)\s*minutes?/)?.[1] || 0);
  const seconds = Number(str.match(/(-?\d+(\.\d+)?)\s*seconds?/)?.[1] || 0);
  return hours * 3600 + minutes * 60 + seconds;
};

const DATA_SIZE_MULTIPLIERS = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3, TB: 1024 ** 4 };
const parseDataSizeToBytes = (value) => {
  if (!value || value === "-") return -1;
  const match = String(value).trim().match(/^([\d.]+)\s*(KB|MB|GB|TB|B)?$/i);
  if (!match) return 0;
  const unit = (match[2] || "B").toUpperCase();
  return parseFloat(match[1]) * (DATA_SIZE_MULTIPLIERS[unit] || 1);
};

const formatDuration = (durationString) => {
  if (!durationString || durationString === "-") return "-";
  // Format: "X sessions - Y hours-Z minutes-W seconds" -> "X Ses - Y H - Z M - W Sec"
  return durationString
    .replace(/sessions/g, "Ses")
    .replace(/hours/g, "H")
    .replace(/minutes/g, "M")
    .replace(/seconds/g, "Sec")
    .replace(/ - /g, " - ");
};

// Data-driven column config so show/hide and sort share one definition
// instead of duplicating per-column JSX. `name` is always visible — it's
// the row's primary identifier — so it's excluded from the picker menu.
const COLUMNS = [
  {
    key: "name",
    label: "Name",
    alwaysVisible: true,
    getValue: (row) => row.name || "",
    render: (row) => <span className="patient-name-link">{row.name || "-"}</span>,
  },
  {
    key: "emailId",
    label: "User name/Email",
    getValue: (row) => row.emailId || "",
    render: (row) => (
      <CustomTooltip title={row.emailId || "-"}>
        <span className="hp-table_truncate">{row.emailId || "-"}</span>
      </CustomTooltip>
    ),
  },
  {
    key: "numberOfUsers",
    label: "Number of Users",
    getValue: (row) => row.numberOfTotalUsers ?? 0,
    render: (row) => `${row.numberOfTotalUsers ?? 0}/${row.numberOfUsers ?? 0}`,
  },
  {
    key: "totalDuration",
    label: "Total Hours",
    getValue: (row) => parseDurationToSeconds(row.totalDuration),
    render: (row) => formatDuration(row.totalDuration || "-"),
  },
  {
    key: "totalDataSize",
    label: "Total Data Size",
    getValue: (row) => parseDataSizeToBytes(row.totalDataSize),
    render: (row) => row.totalDataSize || "-",
  },
  {
    key: "totalRegular25Hz",
    label: "25 Hz Reg",
    getValue: (row) => parseDurationToSeconds(row.totalRegular25Hz),
    render: (row) => formatDuration(row.totalRegular25Hz || "-"),
  },
  {
    key: "totalRaw25Hz",
    label: "25 Hz Raw",
    getValue: (row) => parseDurationToSeconds(row.totalRaw25Hz),
    render: (row) => formatDuration(row.totalRaw25Hz || "-"),
  },
  {
    key: "totalRaw100Hz",
    label: "100 Hz Raw",
    getValue: (row) => parseDurationToSeconds(row.totalRaw100Hz),
    render: (row) => formatDuration(row.totalRaw100Hz || "-"),
  },
  {
    key: "totalOnlineDuration",
    label: "Online",
    getValue: (row) => parseDurationToSeconds(row.totalOnlineDuration),
    render: (row) => row.totalOnlineDuration || "-",
  },
  {
    key: "totalOflineDuration",
    label: "Offline",
    getValue: (row) => parseDurationToSeconds(row.totalOflineDuration),
    render: (row) => row.totalOflineDuration || "-",
  },
  {
    key: "deviceCount",
    label: "Assigned Device Count",
    getValue: (row) => (Array.isArray(row.deviceModel) ? row.deviceModel.length : 0),
    render: (row) => (Array.isArray(row.deviceModel) ? row.deviceModel.length : 0),
  },
  {
    key: "lastUpload",
    label: "Last Upload Date",
    getValue: (row) => new Date(row.getLatestActivityOfPatient?.lastActivityLoggedDate).getTime() || 0,
    render: (row) => (
      <CustomTooltip
        title={`Device: ${row.getLatestActivityOfPatient?.deviceName || "N/A"} | Patient: ${row.getLatestActivityOfPatient?.patientName || "N/A"}`}
      >
        <span>{row.getLatestActivityOfPatient?.lastActivityLoggedDate || "-"}</span>
      </CustomTooltip>
    ),
  },
];

const ALL_COLUMN_KEYS = COLUMNS.map((c) => c.key);

const loadVisibleColumns = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(VISIBLE_COLUMNS_KEY));
    if (Array.isArray(stored) && stored.length) {
      // Guards against a stale list from a previous app version referencing
      // columns that no longer exist, and picks up newly-added columns.
      return stored.filter((key) => ALL_COLUMN_KEYS.includes(key));
    }
  } catch {
    // Ignore malformed/blocked storage and fall back to showing everything.
  }
  return ALL_COLUMN_KEYS;
};

export default function HPTable() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(() => Number(sessionStorage.getItem(PAGE_KEY)) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem(SEARCH_KEY) || "");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const navigate = useNavigate();

  const visibleColumnsSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);

  const displayedColumns = useMemo(
    () => COLUMNS.filter((c) => c.alwaysVisible || visibleColumnsSet.has(c.key)),
    [visibleColumnsSet],
  );

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    sessionStorage.setItem(SEARCH_KEY, debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const fetchAllProfessionalDetail = useCallback(
    async (pageIndex, pageSize, searchString) => {
      setIsLoading(true);
      setFetchError("");
      const payload = {
        pageIndex: pageIndex,
        pageSize: pageSize,
        searchString: searchString,
      };
      try {
        const response = await GetAllProfessionalDetail(payload);
        if (response.statusCode === 200) {
          setRows(response.data);
          setTotalRows(response.totalRecords);
        } else {
          console.error(
            "Error fetching professional details:",
            response.message,
          );
          setRows([]);
          setFetchError(response.message || "Failed to load healthcare professionals");
        }
      } catch (error) {
        console.error("Error fetching professional details:", error?.message);
        setRows([]);
        setFetchError(error?.message || "Failed to load healthcare professionals");
      }
      setIsLoading(false);
    },
    [],
  );

  useEffect(() => {
    fetchAllProfessionalDetail(page, rowsPerPage, debouncedSearchQuery);
  }, [fetchAllProfessionalDetail, debouncedSearchQuery, page, rowsPerPage]);

  const retryFetch = () => {
    fetchAllProfessionalDetail(page, rowsPerPage, debouncedSearchQuery);
  };

  const handleForm = () => {
    navigate("/hpform");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem(PAGE_KEY, String(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlepatients = (row) => {
    navigate(`/superadmin-patients/${row.id}`, { state: { rowData: row } });
  };

  // Sorts only the currently-loaded page — GetAllProfessionalDetail is
  // server-paginated and doesn't accept a sort parameter, so sorting the
  // full 45+ record dataset isn't available without a backend change.
  // Cycles asc -> desc -> cleared (back to unsorted) on repeated clicks;
  // clicking a different column always starts that column fresh at asc.
  const handleSort = (key) => {
    if (sortColumn !== key) {
      setSortColumn(key);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      setSortColumn(null);
      setSortDirection("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortColumn) return rows;
    const column = COLUMNS.find((c) => c.key === sortColumn);
    if (!column) return rows;
    const sorted = rows.toSorted((a, b) => {
      const av = column.getValue(a);
      const bv = column.getValue(b);
      if (typeof av === "string" || typeof bv === "string") {
        return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
      }
      return av - bv;
    });
    if (sortDirection === "desc") sorted.reverse();
    return sorted;
  }, [rows, sortColumn, sortDirection]);

  const toggleColumn = (key) => {
    const next = visibleColumns.includes(key)
      ? visibleColumns.filter((k) => k !== key)
      : [...visibleColumns, key];
    localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify(next));
    setVisibleColumns(next);
  };

  return (
    <div className="hp-table">
      <div className="hp-table_headerhp">
        <div className="hp-table_headerhp_left">
          <SearchInput
            className="hp-table_headerhp_left_searchbar"
            name="searchQuery"
            placeholder="Search by Name or Email"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
          <div className="hp-table_headerhp_right">
            <Buttons
              text="Columns"
              type="button"
              variant="smallsecondary"
              style={{ height: 40 }}
              onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
              icon={<ViewColumnIcon fontSize="small" />}
            />
            <Menu
              anchorEl={columnMenuAnchor}
              open={Boolean(columnMenuAnchor)}
              onClose={() => setColumnMenuAnchor(null)}
            >
              {COLUMNS.reduce((acc, c) => {
                if (c.alwaysVisible) return acc;
                acc.push(
                  <MenuItem key={c.key} onClick={() => toggleColumn(c.key)} dense>
                    <Checkbox checked={visibleColumnsSet.has(c.key)} size="small" />
                    <ListItemText primary={c.label} />
                  </MenuItem>,
                );
                return acc;
              }, [])}
            </Menu>
            <Buttons
              text="Add Healthcare Professionals"
              type="button"
              variant="primary"
              fullWidth={false}
              style={{ height: 40 }}
              onClick={handleForm}
              icon={<PlusIcon />}
            />
          </div>
        </div>
        <div className="table-container">
          <TableContainer component={Paper}>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell>SL NO</TableCell>
                  {displayedColumns.map((c) => (
                    <TableCell key={c.key}>
                      <TableSortLabel
                        active={sortColumn === c.key}
                        direction={sortColumn === c.key ? sortDirection : "asc"}
                        onClick={() => handleSort(c.key)}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows?.length === 0 ? (
                  <TableStatusRow
                    colSpan={1 + displayedColumns.length}
                    loading={isLoading}
                    error={fetchError}
                    onRetry={retryFetch}
                    emptyMessage="No healthcare professionals found"
                  />
                ) : (
                  sortedRows.map((row, index) => (
                    <TableRow
                      key={row.id}
                      onClick={() => handlepatients(row)}
                      style={{ cursor: "pointer" }}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      {displayedColumns.map((c) => (
                        <TableCell key={c.key}>
                          {c.render(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        <div className="table-pagination">
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>

      </div>
  );
}
